import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Copy, Check, ThumbsUp, ThumbsDown, Volume2 } from 'lucide-react'

import { formatShortTimestamp } from '@helpers/format'
import SecureMarkdown from '@components/SecureMarkdown'
import Image from '@components/Image'
import { Button } from '@components/ui/button'
import { useFlashMessage } from '@components/FlashMessage'

import { useCurrentUser } from '@contexts/currentUser'

import AudioControls from './AudioControls'

import { useAnalytics } from '@contexts/analytics'

const getModerationMessage = (moderationLabel) => {
  switch (moderationLabel) {
    case 'SELF_HARM':
      return "Your wellbeing is important to us. If you're experiencing difficulties, we encourage you to reach out to a mental health professional or trusted support person who can provide appropriate assistance."
    case 'MENTAL_HEALTH':
      return "We recognize the importance of mental health. If you'd like to discuss your feelings, we encourage you to connect with a mental health professional or support service that can provide appropriate guidance."
    case 'VIOLENCE':
      return "We maintain a respectful and constructive environment. If you're experiencing challenges, we encourage you to seek appropriate support services that can help address your concerns."
    case 'SUBSTANCE_ABUSE':
      return "We prioritize health and safety. If you have questions about substance use, we encourage you to seek information from qualified health professionals or support services."
    case 'WEAPONS':
      return "We maintain a safe and secure environment for all users. Let\'s focus our discussions on constructive and appropriate topics that support learning and development."
    case 'ILLEGAL_ACTIVITY':
      return "We maintain a platform focused on appropriate and lawful discussions. If you need guidance, we encourage you to seek information from relevant authorities or support services."
    case 'SEXUAL_CONTENT':
      return "This content is not appropriate for our platform. We encourage discussions that align with our community guidelines and educational objectives."
    case 'HARASSMENT':
      return "We maintain a respectful and inclusive environment. All users are expected to engage in constructive and appropriate interactions."
    case 'PROFANITY':
      return "We encourage the use of appropriate and professional language. Please revise your message to align with our community standards."
    case 'THREATS':
      return "We maintain a safe and respectful environment. If you're experiencing concerns, we encourage you to seek appropriate support services or authorities."
    default:
      return "Sorry, I can't respond to that."
  }
}

const Message = ({
  id,
  status,
  from,
  text,
  moderationLabel,
  sourceName,
  sourceLink,
  createdAt,
  audioPlayerRef,
  currentAudioMessageId,
  textToSpeechMutation,
  trackAudioPlayed,
  attachments = []
}) => {
  const [isCopied, setIsCopied] = useState(false)
  const [isThumbsUp, setIsThumbsUp] = useState(false)
  const [isThumbsDown, setIsThumbsDown] = useState(false)
  const { track } = useAnalytics()

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text)
  }

  const { currentMembership: { organization } } = useCurrentUser()
  const customModerationEnabled = organization.features.includes('moderation_v2')

  return (
    <>
      <If condition={customModerationEnabled && status === 'MODERATION'}>
        <div className='flex flex-col max-w-[75%] mb-3 self-start'>
          <div className='w-full flex flex-row self-end'>
            <div className='w-full rounded-bl-none overflow-x-auto bg-background border-border'>
              <SecureMarkdown content={getModerationMessage(moderationLabel)} />
            </div>
          </div>
        </div>
      </If>

      <div className={twMerge('flex flex-col max-w-[75%] mb-3', from === 'USER' ? 'self-end' : 'self-start')}>
        <Choose>
          <When condition={from === 'ASSISTANT'}>

            <div className='w-full flex flex-row self-end'>
              <div className='w-full rounded-bl-none overflow-x-auto bg-background border-border'>
                <SecureMarkdown content={text} />
              </div>
            </div>

            <If condition={sourceName && sourceLink}>
              <div className='flex justify-left'>
                <a href={sourceLink} className='mt-1 text-blue-500 text-sm'>
                  Computed by {sourceName}
                </a>
              </div>
            </If>

            <div className='flex'>
              <Button
                variant="ghost"
                size="icon"
                aria-label='Copy text to clipboard'
                className='w-fit rounded-md p-1 cursor-pointer hover:bg-accent hover:text-primary'
                onClick={() => {
                  copyToClipboard()
                  setIsCopied(true)
                  setTimeout(() => setIsCopied(false), 1000)
                }}
              >
                {isCopied ? <Check className='h-5 w-5 text-primary/50' /> : <Copy data-tutorial='copy-message-step' className='h-5 w-5 text-primary/50' />}
              </Button>


              <Button
                variant="ghost"
                size="icon"
                aria-label="Thumbs up feedback"
                className={twMerge('w-fit rounded-md p-1 cursor-pointer hover:bg-accent hover:text-primary', isThumbsUp && '')}
                onClick={() => {
                  setIsThumbsUp(!isThumbsUp)
                  setIsThumbsDown(false)
                  track('Chat Message Feedback', {
                    message_id: id,
                    feedback: 'positive',
                    message: text
                  })
                }}
              >
                <ThumbsUp className={`h-5 w-5 text-primary/50 ${isThumbsUp ? 'fill-current fill-primary/50' : ''}`} />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                aria-label="Thumbs down feedback"
                className={twMerge('w-fit rounded-md p-1 cursor-pointer hover:bg-accent hover:text-primary', isThumbsDown && 'text-primary')}
                onClick={() => {
                  setIsThumbsDown(!isThumbsDown)
                  setIsThumbsUp(false)
                  track('Chat Message Feedback', {
                    message_id: id,
                    feedback: 'negative',
                    message: text
                  })
                }}
              >
                <ThumbsDown className={`h-5 w-5 text-primary/50 ${isThumbsDown ? 'fill-current fill-primary/50' : ''}`} />
              </Button>


              <AudioControls
                messageId={id}
                audioPlayerRef={audioPlayerRef}
                currentAudioMessageId={currentAudioMessageId}
                textToSpeechMutation={textToSpeechMutation}
              />
            </div>


            {/* <small className='self-end mt-1 ml-auto'>{formatShortTimestamp(createdAt)}</small> */}
          </When>

          <Otherwise>
            <If condition={attachments.length > 0}>
              <div className='flex gap-3 mb-3'>
                <For each='attachment' of={attachments}>
                  <Image
                    className='ml-auto object-cover w-auto max-h-[200px] rounded-lg'
                    placeholder={<div className='w-[300px] h-[200px] bg-muted animate-pulse rounded-lg' />}
                    key={attachment.filename}
                    src={attachment.url}
                    alt={attachment.filename}
                  />
                </For>
              </div>
            </If>

            <If condition={text}>
              <div className='w-auto ml-auto flex flex-row self-end'>
                <div className={twMerge(
                  'w-auto border p-2 overflow-x-auto rounded-lg',
                  !customModerationEnabled && status === 'MODERATION'
                    ? 'bg-orange-100 dark:bg-orange-600/20 border-gray-500 dark:border-orange-600 rounded-br-none'
                    : 'border-border bg-accent text-black dark:text-white rounded-br-none'
                )}
                >
                  <SecureMarkdown content={text} />
                </div>
              </div>
            </If>

            <If condition={!customModerationEnabled && status === 'MODERATION'}>
              <small className='self-end mt-1'>Sorry, I can't respond to this message</small>
            </If>

            {/* <small className='self-end mt-1 ml-auto'>{formatShortTimestamp(createdAt)}</small> */}
          </Otherwise>
        </Choose>
      </div >
    </>
  )
}

export default Message
